import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSetRecoilState } from 'recoil';
import {
  CompanyDto,
  CollectiveAgreementDto,
  BusinessSectorDto,
} from '@jaam/commons/dtos';
import CompanyServicesAPI from '../services/api/company/company.service';
import { companyState } from '../state/companyState';
import { useTranslation } from 'react-i18next';

import {
  AppErrorResponse,
  MutationParams,
  useAppMutation,
  UseParamsQuery,
} from '@jaam/web-core';

const handleQuerySuccess = <T>(data: T, onSuccess?: (data: T) => void) => {
  if (onSuccess) onSuccess(data);
};

const handleQueryError = (
  error: AppErrorResponse,
  onError?: (error: AppErrorResponse) => void,
) => {
  if (onError) onError(error);
};

export const useGetCompanies = ({
  onSuccess,
  onError,
}: UseParamsQuery<CompanyDto[]> = {}) => {
  const {
    data: companies,
    isLoading: isFetchingCompanies,
    error: companiesError,
    refetch,
  } = useQuery<CompanyDto[], AppErrorResponse>({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await CompanyServicesAPI.list();
      return response.data;
    },
  });
  if (onSuccess && companies) onSuccess(companies);
  if (onError && companiesError) onError(companiesError);

  return { companies, isFetchingCompanies, companiesError, refetch };
};

export const useGetCollectiveAgreements = ({
  onSuccess,
  onError,
}: UseParamsQuery<{ value: number | undefined; label: string }[]> = {}) => {
  const { t } = useTranslation();

  const {
    data: collectiveAgreements,
    isLoading: isFetchingCollectiveAgreements,
    error: agreementsError,
    refetch,
  } = useQuery<
    CollectiveAgreementDto[],
    AppErrorResponse,
    { value: number; label: string }[]
  >({
    queryKey: ['collective-agreements'],
    queryFn: async () => {
      const response = await CompanyServicesAPI.getCollectiveAgreements();
      return response.data;
    },
    select: (data) => {
      return (data ?? []).map((agreement: CollectiveAgreementDto) => ({
        value: agreement.id,
        label: t(`collectiveAgreements.${agreement.code}`),
      }));
    },
    staleTime: Infinity,
  });

  if (onSuccess && collectiveAgreements) onSuccess(collectiveAgreements);
  if (onError && agreementsError) onError(agreementsError);

  return {
    collectiveAgreements,
    isFetchingCollectiveAgreements,
    agreementsError,
    refetch,
  };
};

export const useGetBusinessSectors = ({
  onSuccess,
  onError,
}: UseParamsQuery<{ value: number; label: string }[]> = {}) => {
  const { t } = useTranslation();

  const {
    data: businessSectors,
    isLoading: isFetchingBusinessSectors,
    error: sectorsError,
    refetch,
  } = useQuery<BusinessSectorDto[], AppErrorResponse, { value: number; label: string }[]>({
    queryKey: ['business-sectors'],
    queryFn: async () => {
      const response = await CompanyServicesAPI.getBusinessSectors();
      return response.data;
    },
    select: (data) => {
      return (data ?? []).map((sector: BusinessSectorDto) => ({
        value: sector.id || 0,
        label: t(`businessSectors.${sector.code}`),
      }));
    },
    staleTime: Infinity,
  });

  if (onSuccess && businessSectors) onSuccess(businessSectors);
  if (onError && sectorsError) onError(sectorsError);

  return { businessSectors, isFetchingBusinessSectors, sectorsError, refetch };
};

export const useAddCompany = ({
  onSuccess,
  onError,
  setError,
}: MutationParams = {}) => {
  const queryClient = useQueryClient();
  const setState = useSetRecoilState(companyState);

  const {
    mutate: addCompany,
    isPending: isAddingCompany,
    isError: isAddCompanyError,
    error: addCompanyError,
  } = useAppMutation<CompanyDto, CompanyDto>({
    mutationFn: async (companyDto) => {
      const { data } = await CompanyServicesAPI.add(companyDto);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setState((prev) => ({
        ...prev,
        companies: [...prev.companies, data],
        activeCompany:
          prev.createWithModal || prev.create ? data : prev.activeCompany,
        create: false,
      }));
      handleQuerySuccess(undefined, onSuccess);
    },
    onError: (error) => handleQueryError(error, onError),
    setError,
  });

  return { addCompany, isAddingCompany, isAddCompanyError, addCompanyError };
};

export const useUpdateCompany = ({
  onSuccess,
  onError,
  setError,
}: MutationParams = {}) => {
  const queryClient = useQueryClient();
  const setState = useSetRecoilState(companyState);

  const {
    mutate: updateCompany,
    isPending: isUpdatingCompany,
    isError: isUpdateCompanyError,
    error: updateCompanyError,
  } = useAppMutation<
    CompanyDto,
    { id: number; companyDto: Partial<CompanyDto> }
  >({
    mutationFn: async ({ id, companyDto }) => {
      const { data } = await CompanyServicesAPI.update(id, companyDto);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['company', data.id] });
      setState((prev) => {
        const updatedCompanies = prev.companies.map((company) =>
          company.id === data.id ? { ...company, ...data } : company,
        );
        return {
          ...prev,
          companies: updatedCompanies,
          activeCompany: { ...prev.activeCompany, ...data } as CompanyDto,
        };
      });
      handleQuerySuccess(undefined, onSuccess);
    },
    onError: (error) => handleQueryError(error, onError),
    setError,
  });

  return {
    updateCompany,
    isUpdatingCompany,
    isUpdateCompanyError,
    updateCompanyError,
  };
};

export const useClearCompanyState = () => {
  const setState = useSetRecoilState(companyState);

  const clearCompany = () => {
    setState((prev) => ({
      ...prev,
      companies: [],
      activeCompany: undefined,
      create: false,
      isUnique: true,
      isUniqueData: null,
    }));
  };

  return { clearCompany };
};

export const useCheckFieldUnique = () => {
  const setState = useSetRecoilState(companyState);

  const checkFieldUnique = async (field: keyof CompanyDto, value: string) => {
    try {
      const { data } = await CompanyServicesAPI.findOne({ [field]: value });
      setState((prev) => ({
        ...prev,
        isUnique: !!data,
        isUniqueData: { [field]: value },
      }));
      return !!data;
    } catch (error: any) {
      return false;
    }
  };

  return { checkFieldUnique };
};
